import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { formatLongDateWithTime } from "utilities";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import { ButtonSecondary } from "components/Atoms/Button";
import { AccessTypesList } from "./AccessTypesList";
import { useOpenNewAccessTypeSidePanel } from "./NewAccessTypeSidePanel";
import { getUpdateGuestlistConfigInstruction, GuestlistConfigFormState } from "shared/utils/fieldsHelper/guestlistFields";

interface GuestlistConfigSidePanelDetailsProps {
  data: server.dto.GuestlistSidePanel;
}

export const GuestlistConfigSidePanelDetails = ({ data }: GuestlistConfigSidePanelDetailsProps) => {
  const openNewAccessTypeSidePanel = useOpenNewAccessTypeSidePanel();
  const [formState, setFormState] = useState<GuestlistConfigFormState>({
    closingDate: moment.utc(data.configuration.deadlineUtc),
    allowMultipleGuestsPerName: data.configuration.multipleGuestsPerName,
    email: data.configuration.guestEmailAddressFormElementBehavior,
    mobileNumber: data.configuration.guestMobileNumberFormElementBehavior,
    passport: data.configuration.guestPassportNumberFormElementBehavior,
    notes: data.configuration.guestNotesFormElementBehavior,
  });

  useEffect(() => {
    setFormState({
      closingDate: moment.utc(data.configuration.deadlineUtc),
      allowMultipleGuestsPerName: data.configuration.multipleGuestsPerName,
      email: data.configuration.guestEmailAddressFormElementBehavior,
      mobileNumber: data.configuration.guestMobileNumberFormElementBehavior,
      passport: data.configuration.guestPassportNumberFormElementBehavior,
      notes: data.configuration.guestNotesFormElementBehavior,
    });
  }, [data.configuration]);

  const onValueChanged = (key: string, value: any) => {
    if (key === "closingDate") {
      setFormState(prevState => ({
        ...prevState,
        closingDate: value,
      }));
    } else if (key === "allowMultipleGuestsPerName") {
      setFormState(prevState => ({
        ...prevState,
        allowMultipleGuestsPerName: value,
      }));
    } else if (key === "email") {
      setFormState(prevState => ({
        ...prevState,
        email: value,
      }));
    } else if (key === "mobileNumber") {
      setFormState(prevState => ({
        ...prevState,
        mobileNumber: value,
      }));
    } else if (key === "passport") {
      setFormState(prevState => ({
        ...prevState,
        passport: value,
      }));
    } else if (key === "notes") {
      setFormState(prevState => ({
        ...prevState,
        notes: value,
      }));
    }
  }

  const cannotEditAllowMultipleGuestsPerName = data.configuration.hasGuestsWithMultipleGuestsPerName;
  const cannotSetEmailAddressAsMandatory = data.configuration.hasGuestsWithoutEmailAddress;
  const cannotSetMobileNumberAsMandatory = data.configuration.hasGuestsWithoutMobileNumber;
  const cannotSetPassportNumberAsMandatory = data.configuration.hasGuestsWithoutPassportNumber;
  const cannotSetNotesAsMandatory = data.configuration.hasGuestsWithoutNotes;

  return <SidePanelDetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      key: "settings",
      type: "regular",
      hideTitle: true,
      title: RESX.Guestlist.ClosingDate,
      fields: [{
        key: "closingDate",
        label: RESX.Guestlist.ClosingDate,
        render: () => <div className="flex-column">
          <div>{formatLongDateWithTime(formState.closingDate)}</div>
        </div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
            value={value}
            onChange={v => onChange(v)}
            validatorKey="closingDate"
            minDate={moment.utc(serverReference.constants.appointment.minDate)}
            maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
            onEnter={submit}
            onEsc={cancel}
          />,
          type: "confirmOrCancelButton",
          fullWidth: true,
          initialValue: formState.closingDate,
          submitChange: (closingDate: moment.Moment) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateGuestlistConfigInstruction(data.configuration.guestlistId, { ...formState, closingDate })
          ),
        }
      }]
    }, {
      key: "guestForm",
      type: "regular",
      title: RESX.Guestlist.Guest,
      fields: [{
        key: "allowMultipleGuestsPerName",
        label: RESX.Guestlist.AllowPlus1,
        render: () => <YesNoText value={formState.allowMultipleGuestsPerName} />,
        inPageEdit: cannotEditAllowMultipleGuestsPerName ? undefined : {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={v => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.allowMultipleGuestsPerName,
          submitChange: (allowMultipleGuestsPerName: boolean) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateGuestlistConfigInstruction(data.configuration.guestlistId, { ...formState, allowMultipleGuestsPerName }),
          ),
        },
      }, {
        key: "email",
        label: RESX.Email.EmailAddress,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.email)?.label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetEmailAddressAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.email,
          submitChange: (email: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateGuestlistConfigInstruction(data.configuration.guestlistId, { ...formState, email })
          ),
        }
      }, {
        key: "mobileNumber",
        label: RESX.ContactDetails.PhoneNumber,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.mobileNumber)?.label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetMobileNumberAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.mobileNumber,
          submitChange: (mobileNumber: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateGuestlistConfigInstruction(data.configuration.guestlistId, { ...formState, mobileNumber })
          ),
        }
      }, {
        key: "passport",
        label: RESX.Passport.PassportNumber,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.passport)?.label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetPassportNumberAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.passport,
          submitChange: (passport: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateGuestlistConfigInstruction(data.configuration.guestlistId, { ...formState, passport })
          ),
        }
      }, {
        key: "notes",
        label: RESX.Note.Notes,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.notes).label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetNotesAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.notes,
          submitChange: (notes: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateGuestlistConfigInstruction(data.configuration.guestlistId, { ...formState, notes })
          ),
        }
      }]
    }]}
    additionalContent={<>
      <div className="flex">
        <h3 className="grow1">{RESX.Guestlist.Lists}</h3>
        <ButtonSecondary
          label={RESX.Buttons.Add}
          onClick={() => openNewAccessTypeSidePanel(data.configuration.guestlistId, () => { })}
        />
      </div>
      <AccessTypesList
        lists={data.lists}
        guestlistId={data.configuration.guestlistId}
      />
    </>}
  />
}

interface GuestlistFieldBehaviorOption {
  value: server.dto.FormElementBehaviors;
  label: string;
}

const optionalGuestlistFieldBehaviorOptions: GuestlistFieldBehaviorOption[] = [
  { value: "notVisible", label: RESX.Advancing.Hidden },
  { value: "visibleOptional", label: RESX.Advancing.Optional },
  { value: "visibleMandatory", label: RESX.Advancing.Required },
]